import { Component, OnInit, OnDestroy, inject, signal, computed, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterModule, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import {
  ProjectService,
  PrismaStats,
  CompletedProjectPaper,
} from '../../../services/project.service';
import { ToastService } from '../../../services/toast.service';
import { Project } from '../../../dtos/project';
import { CompletedAgreementQualityOverviewComponent } from '../completed-agreement-quality-overview/completed-agreement-quality-overview.component';
import { ImportJob } from '../../../project-corpus/domain/types';
import { SearchRecordComponent } from '../search-record/search-record.component';
import { ProjectProtocol } from '../../../dtos/project-protocol';

@Component({
  selector: 'app-project-completed',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    CompletedAgreementQualityOverviewComponent,
    SearchRecordComponent,
  ],
  templateUrl: './project-completed.component.html',
  styleUrls: ['./project-completed.component.scss'],
})
export class ProjectCompletedComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private projectService = inject(ProjectService);
  private toast = inject(ToastService);

  readonly projectId = signal<string>('');
  readonly project = signal<Project | null>(null);
  readonly prismaStats = signal<PrismaStats | null>(null);
  readonly importJobs = signal<ImportJob[]>([]);
  readonly loading = signal<boolean>(true);
  readonly error = signal<string>('');
  readonly protocol = signal<ProjectProtocol | null>(null);

  readonly inclusionCriteriaList = computed(() => {
    const criteria = this.protocol()?.searchCriteria || this.project()?.inclusionCriteria;
    return this.parseCriteriaList(criteria);
  });

  readonly exclusionCriteriaList = computed(() => {
    const criteria = this.project()?.exclusionCriteria;
    return this.parseCriteriaList(criteria);
  });

  readonly sortedReasons = computed(() => {
    const breakdown = this.prismaStats()?.exclusionReasonsBreakdown;
    if (!breakdown) return [];
    return Object.entries(breakdown)
      .map(([reason, count]) => ({ reason, count }))
      .sort((a, b) => b.count - a.count);
  });

  readonly sortedDatabases = computed(() => {
    const breakdown = this.prismaStats()?.databasesBreakdown;
    if (!breakdown) return [];
    return Object.entries(breakdown)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
  });

  // Bibliography / Study Search signals
  readonly papers = signal<CompletedProjectPaper[]>([]);
  readonly totalPapers = signal(0);
  readonly page = signal(0);
  readonly pageSize = signal(10);
  readonly searchQuery = signal('');
  readonly loadingPapers = signal(false);
  readonly papersError = signal<string>('');

  // Debounced search input and stale-response protection
  private readonly searchInput$ = new Subject<string>();
  private searchSub?: Subscription;
  private loadSeq = 0;

  constructor() {
    this.searchSub = this.searchInput$
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe((value) => {
        this.searchQuery.set(value);
        this.page.set(0);
      });

    // Effect to reload papers when page or searchQuery changes
    effect(
      () => {
        const id = this.projectId();
        if (id) {
          this.loadPapers(id);
        }
      },
      { allowSignalWrites: true },
    );
  }

  ngOnDestroy(): void {
    this.searchSub?.unsubscribe();
  }

  ngOnInit(): void {
    // In child route, get parent projectId if not present in active route params
    const pId =
      this.route.snapshot.paramMap.get('projectId') ||
      this.route.parent?.snapshot.paramMap.get('projectId');
    if (pId) {
      this.projectId.set(pId);
      this.loadData();
    } else {
      this.error.set('No project ID found in route.');
      this.loading.set(false);
    }
  }

  loadData(): void {
    this.loading.set(true);
    this.projectService.getProjectById(this.projectId()).subscribe({
      next: (p) => {
        this.project.set(p);
        // Search record is independent of PRISMA stats; a failure here must not break the page.
        this.projectService.getImportJobs(this.projectId()).subscribe({
          next: (jobs) => this.importJobs.set(jobs),
          error: (err) => console.error('Failed to load search record', err),
        });
        this.projectService.getPrismaStats(this.projectId()).subscribe({
          next: (stats) => {
            this.prismaStats.set(stats);

            // Fetch the protocol details
            this.projectService.getProjectProtocol(this.projectId()).subscribe({
              next: (prot) => {
                this.protocol.set(prot);
                this.loading.set(false);
              },
              error: (err) => {
                console.error('Failed to load project protocol', err);
                this.loading.set(false);
              },
            });
          },
          error: (err) => {
            console.error('Failed to load PRISMA stats', err);
            this.error.set('Failed to load PRISMA statistics.');
            this.loading.set(false);
          },
        });
      },
      error: (err) => {
        console.error('Failed to load project details', err);
        this.error.set('Failed to load project details.');
        this.loading.set(false);
      },
    });
  }

  private parseCriteriaList(val: string | undefined | null): string[] {
    if (!val) return [];
    try {
      const parsed = JSON.parse(val);
      if (Array.isArray(parsed)) return parsed;
      return [];
    } catch {
      return val
        .split('\n')
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
    }
  }

  loadPapers(projectId: string): void {
    this.loadingPapers.set(true);
    const token = ++this.loadSeq;
    this.projectService
      .getCompletedPapers(projectId, this.page(), this.pageSize(), this.searchQuery())
      .subscribe({
        next: (res) => {
          // Ignore out-of-order responses so a slower, older request cannot
          // overwrite results from a newer one.
          if (token !== this.loadSeq) {
            return;
          }
          this.papers.set(res.items);
          this.totalPapers.set(res.totalItems);
          this.papersError.set('');
          this.loadingPapers.set(false);
        },
        error: (err) => {
          if (token !== this.loadSeq) {
            return;
          }
          console.error('Failed to load completed papers', err);
          this.papersError.set('Failed to load papers. Please try again.');
          this.loadingPapers.set(false);
        },
      });
  }

  onSearchInput(value: string): void {
    this.searchInput$.next(value);
  }

  onPageSizeChange(newSize: string): void {
    const n = Number(newSize);
    if (!Number.isFinite(n) || n <= 0) return;
    this.pageSize.set(n);
    this.page.set(0);
  }

  nextPage(): void {
    if ((this.page() + 1) * this.pageSize() < this.totalPapers()) {
      this.page.update((p) => p + 1);
    }
  }

  prevPage(): void {
    if (this.page() > 0) {
      this.page.update((p) => p - 1);
    }
  }

  viewExtraction(paper: Pick<CompletedProjectPaper, 'paperToProjectId'>): void {
    // Navigate to data extraction for this paper
    // Since the project is COMPLETED, the data extraction component should show it in read-only mode
    void this.router.navigate(['/projects', this.projectId(), 'data-extraction'], {
      queryParams: { reopen: paper.paperToProjectId },
    });
  }

  /** @deprecated use sortedReasons() signal instead */
  getSortedReasons(): { reason: string; count: number }[] {
    return this.sortedReasons();
  }

  /** @deprecated use sortedDatabases() signal instead */
  getSortedDatabases(): { name: string; count: number }[] {
    return this.sortedDatabases();
  }

  exportAsSvg(): void {
    const svgEl = document.getElementById('prisma-svg');
    if (!svgEl) {
      this.toast.error('Export failed', 'PRISMA diagram SVG not found.');
      return;
    }
    const serializer = new XMLSerializer();
    let source = serializer.serializeToString(svgEl);

    // Ensure XML namespaces
    if (!source.match(/^<svg[^>]+xmlns="http:\/\/www\.w3\.org\/2000\/svg"/)) {
      source = source.replace(/^<svg/, '<svg xmlns="http://www.w3.org/2000/svg"');
    }
    if (!source.match(/^<svg[^>]+xmlns:xlink="http:\/\/www\.w3\.org\/1999\/xlink"/)) {
      source = source.replace(/^<svg/, '<svg xmlns:xlink="http://www.w3.org/1999/xlink"');
    }
    // Ensure there's a space if we just added an attribute before another
    source = source.replace(/"xmlns/, '" xmlns');

    // Add XML declaration
    source = '<?xml version="1.0" standalone="no"?>\r\n' + source;

    const blob = new Blob([source], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const downloadLink = document.createElement('a');
    downloadLink.href = url;
    downloadLink.download = `prisma-diagram-${this.projectId()}.svg`;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
    URL.revokeObjectURL(url);
  }

  exportAsPng(): void {
    const svgEl = document.getElementById('prisma-svg') as any;
    if (!svgEl) {
      this.toast.error('Export failed', 'PRISMA diagram SVG not found.');
      return;
    }

    const serializer = new XMLSerializer();
    const source = serializer.serializeToString(svgEl);
    const svgBlob = new Blob([source], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);

    const image = new Image();
    image.onload = () => {
      const canvas = document.createElement('canvas');
      // Scaled up for crisp high resolution printing
      const scale = 2.5;
      const width = svgEl.viewBox?.baseVal?.width || 950;
      const height = svgEl.viewBox?.baseVal?.height || 960;

      canvas.width = width * scale;
      canvas.height = height * scale;

      const context = canvas.getContext('2d');
      if (context) {
        // High quality rendering options
        context.imageSmoothingEnabled = true;
        context.imageSmoothingQuality = 'high';

        // Draw solid white background
        context.fillStyle = '#ffffff';
        context.fillRect(0, 0, canvas.width, canvas.height);

        context.drawImage(image, 0, 0, canvas.width, canvas.height);

        canvas.toBlob((blob) => {
          if (blob) {
            const pngUrl = URL.createObjectURL(blob);
            const downloadLink = document.createElement('a');
            downloadLink.href = pngUrl;
            downloadLink.download = `prisma-diagram-${this.projectId()}.png`;
            document.body.appendChild(downloadLink);
            downloadLink.click();
            document.body.removeChild(downloadLink);
            URL.revokeObjectURL(pngUrl);
          }
        }, 'image/png');
      }
      URL.revokeObjectURL(url);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      this.toast.error('Export failed', 'Failed to render the PRISMA diagram as a PNG.');
    };
    image.src = url;
  }

  exportAsBibtex(): void {
    this.projectService.exportBibtex(this.projectId()).subscribe({
      next: (bibtex) => {
        const blob = new Blob([bibtex], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const downloadLink = document.createElement('a');
        downloadLink.href = url;
        downloadLink.download = `bibliography-${this.projectId()}.bib`;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
        URL.revokeObjectURL(url);
      },
      error: (err) => {
        console.error('Failed to export bibliography', err);
        this.toast.error('Export failed', 'Failed to export bibliography.');
      },
    });
  }

  exportAsCsv(): void {
    this.projectService.exportExtractionCsv(this.projectId()).subscribe({
      next: (blob) => this.downloadBlob(blob, `extraction-${this.projectId()}.csv`),
      error: (err) => {
        console.error('Failed to export extraction CSV', err);
        this.toast.error('Export failed', 'Failed to export extraction data.');
      },
    });
  }

  exportSearchRecordCsv(): void {
    this.projectService.exportSearchRecordCsv(this.projectId()).subscribe({
      next: (blob) => this.downloadBlob(blob, `search-record-${this.projectId()}.csv`),
      error: (err) => {
        console.error('Failed to export search record', err);
        this.toast.error('Export failed', 'Failed to export the search record.');
      },
    });
  }

  private downloadBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
}
